import React, { useMemo, useState } from "react";

import StatsContainer from "../../Dashboard/StatsContainer";
import ContractInfo from "./ContractInfo";
import ContractDetailsTable from "./ContractDetailsTable";
import ContractSpotracSalaryChart from "./ContractSpotracSalaryChart";
import { userTypes } from "../../../utils/constants";
import { useReduxQuery } from "../../../hooks/useReduxQuery";
import { addPlayerDeal, fetchPlayerDeals } from "../../../api/playerDeal";
import {
  CircularProgress,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import setStyles from "../../../setStyles";
import Arrow from "../../../images/Icons/Arrow";
import { isCollegePlayer, isPlayer } from "../../../utils/utils";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
} from "@material-ui/core";
import { useDispatch } from "react-redux";

const useStyles = makeStyles((theme) => ({
  header: {
    color: "#A28C62",
    ...setStyles.sofiaProBold,
    fontSize: "11px",
    textTransform: "uppercase",
  },
  cell: {
    color: "#05070B",
    fontSize: "14px",
    ...setStyles.sofiaProRegular,
  },
  arrow: {
    paddingLeft: "16px",
  },
  CBAConnectContainer2: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "350px",
    maxWidth: "100%",
    height: "50px",
    backgroundColor: "#3867D620",
    borderRadius: "46px",
    border: "none",
    fontSize: "18px",
    ...setStyles.sofiaProBold,
    cursor: "pointer",
    textDecoration: "none",
    marginTop: "30px",
  },
  CBAConnect2: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "350px",
    maxWidth: "100%",
    height: "50px",
    color: "#3867D6",
    fontSize: "18px",
    ...setStyles.sofiaProBold,
    cursor: "pointer",
    textDecoration: "none",
  },
  addButton: {
    backgroundColor: "#A28C62",
    color: "white",
    marginTop: "16px",
    "&:hover": {
      backgroundColor: "#8a7752",
    },
  },
  formField: {
    marginBottom: "16px",
    width: "100%",
  },
}));

const formatMoney = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

const DetailsContainer = ({ selected, user, parent, page }) => {
  return (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        // display: "flex",
        marginTop: "60px",
      }}
    >
      <StatsContainer
        contract2={true}
        selected={selected}
        user={user}
        parent={parent}
        page={page}
      />
      <ContractInfo selected={selected} user={user} />
      {(user?.userType === userTypes.PLAYER ||
        user?.userType === userTypes.COLLEGE ||
        selected) && [
        <ContractDetailsTable selected={selected} user={user} key={"1"} />,
        <div
          style={{
            width: "100%",
            justifyContent: "center",
            display: "flex",
          }}
        >
          <ContractSpotracSalaryChart user={user} selected={selected} />
        </div>,
      ]}
    </div>
  );
};

export const DealsTable = ({ user, userType }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [openAddDealDialog, setOpenAddDealDialog] = useState(false);
  const [newDeal, setNewDeal] = useState({
    company: "",
    duration: "",
    totalValue: "",
    weeklyPay: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { loadingPlayerDeals, playerDeals } = useReduxQuery("PlayerDeals", () =>
    fetchPlayerDeals(user.id)
  );

  const deals = Object.values(playerDeals || {}).filter(
    (pd) => Number(pd.userId) === Number(user.id)
  );

  const totalWeek = deals?.length || 0;
  const totalWeeklyPay = deals.reduce((acc, deal) => acc + deal.weeklyPay, 0);
  const totalValue = deals.reduce((acc, deal) => acc + deal.totalValue, 0);

  const dealValueUrl = useMemo(() => {
    let url = "/deal-value";
    if (userType === userTypes.AGENT) url += `?playerId=${user?.id}`;
    return url;
  }, [userType, user]);

  const handleAddDealOpen = () => {
    setOpenAddDealDialog(true);
  };

  const handleAddDealClose = () => {
    setOpenAddDealDialog(false);
    setNewDeal({
      company: "",
      duration: "",
      totalValue: "",
      weeklyPay: "",
    });
  };

  const handleDealInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "totalValue" || name === "weeklyPay") {
      // Remove non-numeric characters for processing
      const numericValue = value.replace(/[^0-9]/g, "");

      setNewDeal({
        ...newDeal,
        [name]: numericValue ? Number(numericValue) : "",
      });
    } else {
      setNewDeal({
        ...newDeal,
        [name]: value,
      });
    }
  };

  const formatNumberWithCommas = (value) => {
    if (!value && value !== 0) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleSubmitDeal = async () => {
    try {
      setIsSubmitting(true);
      await dispatch(
        addPlayerDeal({
          ...newDeal,
          userId: user.id,
          isAgent: userType === userTypes.AGENT,
        })
      );

      dispatch(fetchPlayerDeals(user.id));
      handleAddDealClose();
    } catch (error) {
      alert("Error submitting deal");
      console.error("Error submitting deal:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        marginTop: "48px",
        marginBottom: "48px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
        }}
      >
        <h5 style={{ textTransform: "uppercase" }}>Marketing Deals</h5>
        {(userType === userTypes.AGENT ||
          user?.userType === userTypes.AGENT ||
          userType === userTypes.PLAYER ||
          user?.userType === userTypes.PLAYER) && (
          <Button
            variant="contained"
            className={classes.addButton}
            onClick={handleAddDealOpen}
          >
            Add Marketing Deal
          </Button>
        )}
      </div>
      <Paper
        elevation={4}
        style={{
          marginTop: "24px",
        }}
      >
        {loadingPlayerDeals ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "80px",
            }}
          >
            <CircularProgress />
          </div>
        ) : deals.length > 0 ? (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.header} component="th">
                      Company
                    </TableCell>
                    <TableCell className={classes.header} component="th">
                      Deal Length
                    </TableCell>
                    <TableCell className={classes.header} component="th">
                      Total Value
                    </TableCell>
                    <TableCell className={classes.header} component="th">
                      Weekly Pay
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deals.map((deal) => (
                    <TableRow key={deal.id}>
                      <TableCell className={classes.cell}>
                        {deal.company}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {deal.duration}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {isNaN(deal.totalValue)
                          ? "-"
                          : `${formatMoney(deal.totalValue)}`}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {isNaN(deal.weeklyPay)
                          ? "-"
                          : `${formatMoney(deal.weeklyPay)}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <p>No deals available</p>
          </div>
        )}
      </Paper>
      {deals.length > 1 && (
        <Paper elevation={4} style={{ marginTop: "24px" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.header} component="th">
                    Total Deals
                  </TableCell>
                  <TableCell className={classes.header} component="th">
                    Total Value
                  </TableCell>
                  <TableCell className={classes.header} component="th">
                    Total Weekly Pay
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.cell}>{totalWeek}</TableCell>
                  <TableCell className={classes.cell}>
                    {isNaN(totalValue) ? "-" : `${formatMoney(totalValue)}`}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {isNaN(totalWeeklyPay)
                      ? "-"
                      : `${formatMoney(totalWeeklyPay)}`}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {isCollegePlayer(user.userType) && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "48px",
          }}
        >
          <div className={classes.CBAConnectContainer2}>
            <a href={dealValueUrl} className={classes.CBAConnect2}>
              {`${
                isPlayer(userType)
                  ? "What are my deals worth?"
                  : "What are their deals worth?"
              }`}
              <div className={classes.arrow}>
                <Arrow color="blue" />
              </div>
            </a>
          </div>
        </div>
      )}

      {/* Add Deal Dialog */}
      <Dialog
        open={openAddDealDialog}
        onClose={handleAddDealClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Marketing Deal</DialogTitle>
        <DialogContent>
          <TextField
            className={classes.formField}
            label="Company"
            name="company"
            value={newDeal.company}
            onChange={handleDealInputChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            className={classes.formField}
            label="Deal Duration (e.g., '6 months', '1 year')"
            name="duration"
            value={newDeal.duration}
            onChange={handleDealInputChange}
            fullWidth
            margin="normal"
            required
            placeholder="e.g., 6 months, 1 year"
          />
          <FormControl className={classes.formField} fullWidth margin="normal">
            <InputLabel htmlFor="totalValue">Total Value</InputLabel>
            <Input
              id="totalValue"
              name="totalValue"
              type="text"
              value={formatNumberWithCommas(newDeal.totalValue)}
              onChange={handleDealInputChange}
              startAdornment={
                <InputAdornment position="start">$</InputAdornment>
              }
            />
          </FormControl>
          <FormControl className={classes.formField} fullWidth margin="normal">
            <InputLabel htmlFor="weeklyPay">Weekly Pay</InputLabel>
            <Input
              id="weeklyPay"
              name="weeklyPay"
              type="text"
              value={formatNumberWithCommas(newDeal.weeklyPay)}
              onChange={handleDealInputChange}
              startAdornment={
                <InputAdornment position="start">$</InputAdornment>
              }
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddDealClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmitDeal}
            color="primary"
            variant="contained"
            disabled={isSubmitting || !newDeal.company || !newDeal.duration}
          >
            {isSubmitting ? "Submitting..." : "Add Deal"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DetailsContainer;
